/**
 * Catálogos de UI + helpers de label da Tela A2 (Config Tenant).
 *
 * Só rotula valores REAIS do backend (R15). Os textos descritivos vêm do
 * mockup `a2-subtelas.jsx`/`a2-perfil-wizard.jsx` mas mapeiam ao shape real
 * (privacyProfile + routingStrategy + 4 perfis + 3 campos de tenant-config).
 */

import type {
  InternalNoteVisibilityCode,
  OperationProfileId,
  PrivacyProfileCode,
  RoutingStrategyCode,
} from './types';

/** Range canônico validado no endpoint (5min … 4h). */
export const CHECKLIST_INTERVAL_MIN_MS = 5 * 60 * 1000;
export const CHECKLIST_INTERVAL_MAX_MS = 4 * 60 * 60 * 1000;

/** Opções de intervalo do checklist-agent (todas dentro do range válido). */
export const CHECKLIST_INTERVAL_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 5 * 60 * 1000, label: '5 minutos' },
  { value: 15 * 60 * 1000, label: '15 minutos' },
  { value: 30 * 60 * 1000, label: '30 minutos' },
  { value: 60 * 60 * 1000, label: '1 hora' },
  { value: 2 * 60 * 60 * 1000, label: '2 horas' },
  { value: 4 * 60 * 60 * 1000, label: '4 horas' },
];

export const INTERNAL_NOTE_VISIBILITY_OPTIONS: readonly {
  value: InternalNoteVisibilityCode;
  label: string;
  hint: string;
}[] = [
  {
    value: 'public_team',
    label: 'Equipe (público)',
    hint: 'Toda a equipe vê as notas internas por padrão.',
  },
  {
    value: 'private_author',
    label: 'Privado · só o autor',
    hint: 'Só quem escreveu vê a nota — recomendado para tenants Regulated.',
  },
  {
    value: 'private_supervisors',
    label: 'Privado · autor + supervisores',
    hint: 'Autor e supervisores da equipe veem; demais atendentes não.',
  },
];

export const PRIVACY_PROFILE_LABELS: Record<PrivacyProfileCode, string> = {
  padrao: 'Padrão',
  misto: 'Misto',
  maxima_privacidade: 'Máxima privacidade',
};

export const ROUTING_STRATEGY_LABELS: Record<RoutingStrategyCode, string> = {
  round_robin: 'Rodízio (round-robin)',
  least_loaded: 'Menor carga',
  skills_weighted: 'Por skills',
  fixed_route: 'Rota fixa',
};

/** Metadados de apresentação dos 4 perfis (do a2-perfil-wizard, mapeados ao backend). */
export const OPERATION_PROFILE_META: Record<
  OperationProfileId,
  { icon: string; name: string; size: string; who: string }
> = {
  solo: {
    icon: '👤',
    name: 'Autônomo Solo',
    size: '1 pessoa atendendo ou microempresa',
    who: 'Profissional liberal · microempresário · solopreneur',
  },
  pequena: {
    icon: '👥',
    name: 'Pequena Empresa',
    size: '5–50 funcionários',
    who: 'ISPs regionais · lojas de pequeno porte · clínicas pequenas',
  },
  media: {
    icon: '🏢',
    name: 'Empresa Média',
    size: '50–500 funcionários · operação balanceada',
    who: 'ISPs grandes · empresas de serviços · hospitais médios',
  },
  grande: {
    icon: '🏛️',
    name: 'Empresa Grande · Regulada',
    size: '500+ funcionários OU vertical regulada',
    who: 'Hospitais grandes · bancos · escritórios de advocacia',
  },
};

/**
 * Labels dos 4 campos reais da aba Geral — mesmo texto dos `<label>` do form
 * (não inventar strings novas). Usado pela `StickySaveBar` (Admin Hub T7) para
 * nomear o que mudou; chaves = `SerializedTenantConfig`/`UpdateTenantConfigRequest`.
 */
export const GENERAL_TAB_FIELD_LABELS: Record<
  | 'checklistAutoFulfillEnabled'
  | 'checklistAgentPeriodicIntervalMs'
  | 'defaultInternalNoteVisibility'
  | 'authorizedContactsDpoOnly'
  | 'insightMinBasedOnCount',
  string
> = {
  checklistAutoFulfillEnabled: 'Auto-preenchimento do checklist',
  checklistAgentPeriodicIntervalMs: 'Intervalo do agente de checklist',
  defaultInternalNoteVisibility: 'Visibilidade padrão de notas internas',
  authorizedContactsDpoOnly: 'Contatos autorizados: somente DPO',
  insightMinBasedOnCount: 'Mínimo de conversas para insights de gestão',
};

/**
 * Labels dos 2 campos reais da aba Acompanhamento Silencioso — mesmo texto dos
 * títulos de cartão já presentes no form (não inventar strings novas). Usado
 * pela `StickySaveBar` (Admin Hub T8) para nomear o que mudou; chaves =
 * `UpdateTenantConfigRequest`.
 */
export const SILENT_TAB_FIELD_LABELS: Record<'businessHours' | 'outOfHoursMessage', string> = {
  businessHours: 'Horário de atendimento',
  outOfHoursMessage: 'Mensagem fora do horário',
};

/**
 * Labels dos 2 campos reais da aba Análise Comportamental — mesmo texto dos
 * títulos de cartão já presentes no form. Usado pela `StickySaveBar` (Admin Hub
 * T8); chaves = `UpdateTenantConfigRequest`.
 */
export const BEHAVIORAL_TAB_FIELD_LABELS: Record<
  'behavioralDetectionEnabled' | 'behavioralMonitoredPatternTypes',
  string
> = {
  behavioralDetectionEnabled: 'Detecção comportamental ativa',
  behavioralMonitoredPatternTypes: 'Padrões monitorados',
};

/**
 * Labels dos 4 campos do form de Notificações do operador — mesmo texto dos
 * títulos de cartão já presentes no form (não inventar strings novas). Usado
 * pela `StickySaveBar` (Admin Hub T9) para nomear o que mudou. Chaves são
 * campos do `FormState` interno de `OperatorShiftTab` — o PATCH real sempre
 * carrega o objeto `operatorNotificationPolicy` inteiro (não há 1 chave de
 * patch por campo), mas o usuário precisa ver O QUE mudou por dentro dele.
 */
export const OPERATOR_SHIFT_TAB_FIELD_LABELS: Record<
  'enabled' | 'timeZone' | 'windows' | 'operatorCanSelfMarkOnCall',
  string
> = {
  enabled: 'Silenciar push fora do expediente',
  timeZone: 'Fuso horário do expediente',
  windows: 'Janelas de expediente',
  operatorCanSelfMarkOnCall: 'Operador pode se auto-marcar de plantão',
};

/**
 * Labels dos 2 campos reais do PATCH da aba Agenda nativa — mesmo texto dos
 * títulos de cartão já presentes no form. Usado pela `StickySaveBar` (Admin
 * Hub T10) para nomear o que mudou; chaves = `UpdateTenantConfigRequest`.
 */
export const SCHEDULING_TAB_FIELD_LABELS: Record<
  'nativeSchedulingModeDefault' | 'waitlistPolicy',
  string
> = {
  nativeSchedulingModeDefault: 'Modo de agendamento pela IA',
  waitlistPolicy: 'Política da fila de espera',
};

/**
 * Labels dos 5 campos do form de Lembretes de agendamento — mesmo texto dos
 * títulos de cartão já presentes no form (não inventar strings novas). Usado
 * pela `StickySaveBar` (Admin Hub T10) para nomear o que mudou. Chaves são
 * campos do `FormState` interno de `RemindersTab` — o PATCH real sempre
 * carrega o objeto `appointmentReminderPolicy` inteiro (não há 1 chave de
 * patch por campo, mesmo padrão de `operatorNotificationPolicy` na T9), mas
 * o usuário precisa ver O QUE mudou por dentro dele.
 */
export const REMINDERS_TAB_FIELD_LABELS: Record<
  'enabled' | 'offsets' | 'followupEnabled' | 'followupDelayHours' | 'mode',
  string
> = {
  enabled: 'Lembretes ativos',
  offsets: 'Antecedências',
  followupEnabled: 'Follow-up pós-conclusão',
  followupDelayHours: 'Quando (follow-up)',
  mode: 'Quem conduz',
};

/** Formata um timestamp ISO para pt-BR; null → "—". */
export function formatUpdatedAt(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
